<template>

  <div>
    <b-row
      class="content-header"
    >

      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title">
              Settings
            </h1>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <!-- Table Container Card -->
    <b-form @submit.prevent="submitForm">
      <validation-observer
        ref="reservationSettingForm"
        #default="{invalid}"
      >
        <b-card
          header-tag="header"
        >
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="SettingsIcon"
                class=""
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Reservations by Agent
            </h3>
          </template>

          <b-row>
            <b-col md="12">
              <b-form-group
                label="Daily Reservations Quota"
                label-cols-md="2"
                label-for="dailyReservationQuota"
                description="The maximum “Reserved” reservations an agent can create per calendar day (00.00am to 11.59pm). Reservations released on the same day will restore the quota for that day and are thus not counted in the quota."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Daily Reservations Quota"
                  vid="dailyReservationQuota"
                  rules="required"
                >
                  <b-form-input
                    id="dailyReservationQuota"
                    v-model="dailyReservationQuota"
                    :state="(errors.length > 0) ? false : null"
                    name="dailyReservationQuota"
                    placeholder="5"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="dailyReservationQuotaValidation"
                    class="text-danger"
                  >
                    {{ dailyReservationQuotaError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Reservation Duration"
                label-cols-md="2"
                label-for="reservationDuration"
                description="The default duration, in hours, of an agent-created reservation."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Reservation Duration"
                  vid="reservationDuration"
                  rules="required"
                >
                  <b-form-input
                    id="reservationDuration"
                    v-model="reservationDuration"
                    :state="(errors.length > 0) ? false : null"
                    name="reservationDuration"
                    placeholder="48"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="reservationDurationValidation"
                    class="text-danger"
                  >
                    {{ reservationDurationError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Minimum Consecutive Interval"
                label-cols-md="2"
                label-for="intervalDuration"
                description="The minimum duration, in hours, an agent must wait before a repeat reservation for the same lot can be created, from release date/time."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Minimum Consecutive Interval"
                  vid="intervalDuration"
                  rules="required"
                >
                  <b-form-input
                    id="intervalDuration"
                    v-model="intervalDuration"
                    :state="(errors.length > 0) ? false : null"
                    name="intervalDuration"
                    placeholder="2"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="intervalDurationValidation"
                    class="text-danger"
                  >
                    {{ intervalDurationError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        <b-card
          header-tag="header"
        >
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="SettingsIcon"
                class=""
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Settings for “Submission Hold” Reservations
            </h3>
          </template>

          <b-row>
            <b-col md="12">
              <b-form-group
                label="Max Concurrent Submission Holds"
                label-cols-md="2"
                label-for="maxConcurrentSubmissionHold"
                description="This refers to how many concurrent lots (different lots) a user can have on “Submission Hold” at any time."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Max Concurrent Submission Holds"
                  vid="maxConcurrentSubmissionHold"
                  rules="required"
                >
                  <b-form-input
                    id="maxConcurrentSubmissionHold"
                    v-model="maxConcurrentSubmissionHold"
                    :state="(errors.length > 0) ? false : null"
                    name="maxConcurrentSubmissionHold"
                    placeholder="5"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="maxConcurrentSubmissionHoldValidation"
                    class="text-danger"
                  >
                    {{ maxConcurrentSubmissionHoldError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Max Released Submission Hold Per Lot (Reservations)"
                label-cols-md="2"
                label-for="maxReleasedSubmissionHoldPerLot"
                description="This limits user to only a fixed number (e.g. 3) of Submission Hold reservations for the same lot within a fixed period (e.g. 24 hours, defined in WaitTimeUntilHoldReset)."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Max Released Submission Hold Per Lot (Reservations)"
                  vid="maxReleasedSubmissionHoldPerLot"
                  rules="required"
                >
                  <b-form-input
                    id="maxReleasedSubmissionHoldPerLot"
                    v-model="maxReleasedSubmissionHoldPerLot"
                    :state="(errors.length > 0) ? false : null"
                    name="maxReleasedSubmissionHoldPerLot"
                    placeholder="2"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="maxReleasedSubmissionHoldPerLotValidation"
                    class="text-danger"
                  >
                    {{ maxReleasedSubmissionHoldPerLotError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Wait Time Until Hold Reset (Hours)"
                label-cols-md="2"
                label-for="waitTimeUntilHoldReset"
                description="This defines the fixed time limit that affects MaxReleasedSubmissionHoldPerLot."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Wait Time Until Hold Reset (Hours)"
                  vid="waitTimeUntilHoldReset"
                  rules="required"
                >
                  <b-form-input
                    id="waitTimeUntilHoldReset"
                    v-model="waitTimeUntilHoldReset"
                    :state="(errors.length > 0) ? false : null"
                    name="waitTimeUntilHoldReset"
                    placeholder="24"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="waitTimeUntilHoldResetValidation"
                    class="text-danger"
                  >
                    {{ waitTimeUntilHoldResetError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Submission Hold Duration (Minutes)"
                label-cols-md="2"
                label-for="submissionHoldDuration"
                description="How long does a Submission Hold reservation lasts"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Submission Hold Duration (Minutes)"
                  vid="submissionHoldDuration"
                  rules="required"
                >
                  <b-form-input
                    id="submissionHoldDuration"
                    v-model="submissionHoldDuration"
                    :state="(errors.length > 0) ? false : null"
                    name="submissionHoldDuration"
                    placeholder="15"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="submissionHoldDurationValidation"
                    class="text-danger"
                  >
                    {{ submissionHoldDurationError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        <b-card
          header-tag="header"
        >
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="SettingsIcon"
                class=""
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Settings for Purchase Options
            </h3>
          </template>

          <b-row>
            <b-col md="12">
              <b-form-group
                label="Default Downpayment (%)"
                label-cols-md="2"
                label-for="defaultDownPaymentPercentage"
                description="The default % downpayment for all products when loading Purchase Options in a sales submission."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Default Downpayment (%)"
                  vid="defaultDownPaymentPercentage"
                  rules="required"
                >
                  <b-form-input
                    id="defaultDownPaymentPercentage"
                    v-model="defaultDownPaymentPercentage"
                    :state="(errors.length > 0) ? false : null"
                    name="defaultDownPaymentPercentage"
                    placeholder="5"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="defaultDownPaymentPercentageValidation"
                    class="text-danger"
                  >
                    {{ defaultDownPaymentPercentageError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        <b-card
          header-tag="header"
        >
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="SettingsIcon"
                class=""
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Settings for Product Type
            </h3>
          </template>

          <b-tabs
            class="purchaser-tabs"
            content-class="mt-2"
          >
            <b-tab
              v-for="productType in productTypes"
              :key="productType"
              :title="productType"
            >
              <b-row class="mb-50">
                <b-col md="4">
                  <span class="">If per {{ productType }} purchased exceed</span>
                </b-col>
                <b-col md="3">
                  <span class="">Maximum Tenure (Months)</span>
                </b-col>
                <b-col md="3">
                  <span class="">Min Down Payment (%)</span>
                </b-col>
              </b-row>
              <b-row
                v-for="(tier, index) in productTypeTenureSettings[productType]"
                :key="index"
                class="align-items-center mb-1"
              >
                <b-col md="4">
                  <b-input-group prepend="$">
                    <b-form-input
                      v-model.number="tier.thresholdAmount"
                      type="number"
                      min="0"
                      placeholder="0"
                    />
                  </b-input-group>
                </b-col>
                <b-col md="3">
                  <v-select
                    v-model="tier.maxTenureMonths"
                    label="title"
                    placeholder="Select tenure"
                    :options="tenureOptions"
                    :reduce="option => option.code"
                    :clearable="false"
                  />
                </b-col>
                <b-col md="3">
                  <b-input-group append="%">
                    <b-form-input
                      v-model.number="tier.minDownPaymentPercentage"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="20"
                    />
                  </b-input-group>
                </b-col>
                <b-col
                  md="2"
                  class="text-right"
                >
                  <feather-icon
                    icon="Trash2Icon"
                    class="removeText"
                    size="18"
                    @click="removeTier(productType, index)"
                  />
                </b-col>
              </b-row>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                type="button"
                variant="primary"
                @click="addTier(productType)"
              >
                Add new
              </b-button>
            </b-tab>
          </b-tabs>
        </b-card>
        <b-nav class="wrap-border save-nav">
          <li
            class="nav-item ml-auto"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              @click="cancelEdit"
            >
              Cancel
            </b-button>
          </li>
          <li
            class="nav-item mr-1"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="success"
              :disabled="invalid"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-50"
              />
              <span class="align-middle">Save Changes</span>
            </b-button>
          </li>
        </b-nav>
      </validation-observer>
    </b-form>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BForm, BButton, BNav, BTabs, BTab, BInputGroup,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BNav,
    BTabs,
    BTab,
    BInputGroup,
    vSelect,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      reservationSetting: {},
      dailyReservationQuota: '',
      dailyReservationQuotaValidation: false,
      dailyReservationQuotaError: '',
      reservationDuration: '',
      reservationDurationValidation: false,
      reservationDurationError: '',
      intervalDuration: '',
      intervalDurationValidation: false,
      intervalDurationError: '',
      maxConcurrentSubmissionHold: '',
      maxConcurrentSubmissionHoldValidation: false,
      maxConcurrentSubmissionHoldError: '',
      maxReleasedSubmissionHoldPerLot: '',
      maxReleasedSubmissionHoldPerLotValidation: false,
      maxReleasedSubmissionHoldPerLotError: '',
      waitTimeUntilHoldReset: '',
      waitTimeUntilHoldResetValidation: false,
      waitTimeUntilHoldResetError: '',
      submissionHoldDuration: '',
      submissionHoldDurationValidation: false,
      submissionHoldDurationError: '',
      defaultDownPaymentPercentage: '',
      defaultDownPaymentPercentageValidation: false,
      defaultDownPaymentPercentageError: '',
      productTypes: ['Niche', 'Pedestal', 'Little Buddha', 'Urn', 'Funeral Service Package'],
      productTypeTenureSettings: {
        Niche: [{ thresholdAmount: 0, maxTenureMonths: null, minDownPaymentPercentage: null }],
        Pedestal: [{ thresholdAmount: 0, maxTenureMonths: null, minDownPaymentPercentage: null }],
        'Little Buddha': [{ thresholdAmount: 0, maxTenureMonths: null, minDownPaymentPercentage: null }],
        Urn: [{ thresholdAmount: 0, maxTenureMonths: null, minDownPaymentPercentage: null }],
        'Funeral Service Package': [{ thresholdAmount: 0, maxTenureMonths: null, minDownPaymentPercentage: null }],
      },
      tenureOptions: [6, 12, 18, 24, 30, 36, 48, 60, 72, 84, 96, 108, 120].map(months => ({ title: `${months} months`, code: months })),
      // validation rules
      required,
    }
  },
  created() {
    this.getReservationSettings()
  },
  methods: {
    getReservationSettings() {
      this.$http.get('reservation/reservation-settings')
        .then(response => {
          this.reservationSetting = response.data
          if (response.data) {
            this.dailyReservationQuota = response.data.dailyReservationQuota
            this.reservationDuration = response.data.reservationDuration
            this.intervalDuration = response.data.intervalDuration
            this.maxConcurrentSubmissionHold = response.data.maxConcurrentSubmissionHold
            this.maxReleasedSubmissionHoldPerLot = response.data.maxReleasedSubmissionHoldPerLot
            this.waitTimeUntilHoldReset = response.data.waitTimeUntilHoldReset
            this.submissionHoldDuration = response.data.submissionHoldDuration
            this.defaultDownPaymentPercentage = response.data.defaultDownPaymentPercentage

            const defaultTier = () => ({ thresholdAmount: 0, maxTenureMonths: null, minDownPaymentPercentage: null })
            const settingsByType = {}
            this.productTypes.forEach(productType => { settingsByType[productType] = [defaultTier()] })
            const savedTenureSettings = response.data.productTypeTenureSettings || []
            savedTenureSettings.forEach(entry => {
              if (settingsByType[entry.productType] !== undefined && entry.tiers && entry.tiers.length) {
                settingsByType[entry.productType] = entry.tiers
              }
            })
            this.productTypeTenureSettings = settingsByType
          }
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    addTier(productType) {
      this.productTypeTenureSettings[productType].push({
        thresholdAmount: 0,
        maxTenureMonths: null,
        minDownPaymentPercentage: null,
      })
    },
    removeTier(productType, index) {
      this.productTypeTenureSettings[productType].splice(index, 1)
    },
    cancelEdit() {
      this.$swal({
        title: 'Are you sure?',
        html: 'Selecting Cancel will <strong>undo any changes</strong> you have made. This action cannot be undone.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning.png'),
        imageWidth: 80,
        imageHeight: 80,
        imageAlt: 'Custom Icon',
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: 'Yes, Cancel!',
        cancelButtonText: 'No, Go Back',
        customClass: {
          confirmButton: 'btn btn-danger ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(result => {
          if (result.value) {
            this.getReservationSettings()
          }
        })
    },
    submitForm() {
      this.$refs.reservationSettingForm.validate().then(success => {
        if (success) {
          const formData = new FormData()

          formData.append('dailyReservationQuota', this.dailyReservationQuota)
          formData.append('reservationDuration', this.reservationDuration)
          formData.append('intervalDuration', this.intervalDuration)
          formData.append('maxConcurrentSubmissionHold', this.maxConcurrentSubmissionHold)
          formData.append('maxReleasedSubmissionHoldPerLot', this.maxReleasedSubmissionHoldPerLot)
          formData.append('waitTimeUntilHoldReset', this.waitTimeUntilHoldReset)
          formData.append('submissionHoldDuration', this.submissionHoldDuration)
          formData.append('defaultDownPaymentPercentage', this.defaultDownPaymentPercentage)

          const productTypeTenureSettingsPayload = this.productTypes.map(productType => ({
            productType,
            tiers: this.productTypeTenureSettings[productType],
          }))
          formData.append('productTypeTenureSettings', JSON.stringify(productTypeTenureSettingsPayload))

          this.$http.patch('reservation/reservation-settings/store', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
            .then(response => {
              if (response.data.data) {
                this.$swal({
                  title: 'Changes have Saved',
                  html: response.data.message || '',
                  // eslint-disable-next-line global-require
                  imageUrl: require('@/assets/images/icons/save.png'),
                  imageWidth: 80,
                  imageHeight: 80,
                  imageAlt: 'Custom Icon',
                  showCancelButton: false,
                  allowOutsideClick: false,
                  confirmButtonText: 'Okay',
                  customClass: {
                    confirmButton: 'btn btn-primary',
                  },
                  buttonsStyling: false,
                })
                  .then(result => {
                    if (result.value) {
                      this.reservationSetting = response.data.data
                    }
                  })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: response.data.message || '',
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
            .catch(error => {
              if (error.data.errors) {
                error.data.errors.forEach(validationError => {
                  if (validationError.param === 'dailyReservationQuota') {
                    this.dailyReservationQuotaError = validationError.msg
                    this.dailyReservationQuotaValidation = true
                  } else if (validationError.param === 'reservationDuration') {
                    this.reservationDurationError = validationError.msg
                    this.reservationDurationValidation = true
                  } else if (validationError.param === 'intervalDuration') {
                    this.intervalDurationError = validationError.msg
                    this.intervalDurationValidation = true
                  } else if (validationError.param === 'maxConcurrentSubmissionHold') {
                    this.maxConcurrentSubmissionHoldError = validationError.msg
                    this.maxConcurrentSubmissionHoldValidation = true
                  } else if (validationError.param === 'maxReleasedSubmissionHoldPerLot') {
                    this.maxReleasedSubmissionHoldPerLotError = validationError.msg
                    this.maxReleasedSubmissionHoldPerLotValidation = true
                  } else if (validationError.param === 'waitTimeUntilHoldReset') {
                    this.waitTimeUntilHoldResetError = validationError.msg
                    this.waitTimeUntilHoldResetValidation = true
                  } else if (validationError.param === 'submissionHoldDuration') {
                    this.submissionHoldDurationError = validationError.msg
                    this.submissionHoldDurationValidation = true
                  } else if (validationError.param === 'defaultDownPaymentPercentage') {
                    this.defaultDownPaymentPercentageError = validationError.msg
                    this.defaultDownPaymentPercentageValidation = true
                  }
                })

                const items = document.getElementsByClassName('text-danger')
                window.scrollTo({
                  top: items[0].offsetTop,
                  behavior: 'smooth',
                })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
  },
}
</script>

<style scoped>
    .removeText {
      color:#D91B35;
      cursor: pointer;
    }
    .addOptionText {
      color: #104D9D;
      cursor: pointer;
    }
    .btn-group-toggle [class*=btn-outline-]:not(:last-child) {
      border-right-width: 1px !important;
    }
    .inlineElement {
      display: flex;
    }
    /* Overrides a global .card-body .input-group rule elsewhere in the app that strips the
       left border off input-group-text and the right border off the adjoining form-control -
       meant for a different card's visual style, but bleeds into this page's $/% tier inputs
       since both live inside .card-body. */
    .card-body .input-group-text {
      border-left: 1px solid #d8d6de;
    }
    .card-body .input-group .form-control {
      border-right: 1px solid #d8d6de !important;
    }
</style>
